import React, { useState } from 'react';
import { Button, TopBar, Field, ReadonlyField } from '../components/ui';
import { rupee } from '../theme';
import { useToast } from '../context/ToastContext';
import { shareText } from '../utils/download';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

// Renders one admin-defined field by its type.
function DynField({ field, value, onChange }) {
  const label = field.label + (field.required ? ' *' : '');

  if (field.type === 'select') {
    return (
      <div className="field">
        <label className="field__label">{label}</label>
        <div className="chip-wrap">
          {(field.options || []).map((opt) => (
            <button key={opt} type="button" onClick={() => onChange(opt)}
              className={`chip ${value === opt ? 'chip--on' : ''}`}>
              {opt}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const numeric = field.type === 'phone' || field.type === 'number';
  return (
    <Field
      label={label}
      value={value || ''}
      inputMode={numeric ? 'numeric' : undefined}
      maxLength={field.type === 'phone' ? 10 : undefined}
      placeholder={field.type === 'date' ? 'DD/MM/YYYY' : field.label}
      onChange={(e) => onChange(numeric ? e.target.value.replace(/\D/g, '') : e.target.value)}
    />
  );
}

// Brochure page-8 "Business Partner Detail" enrolment form.
export default function BecomePartner({ onClose, initialSponsor, fields }) {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [sponsorCode, setSponsorCode] = useState(initialSponsor || '');
  const [formData, setFormData] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // { referralCode, perJoinAmount }

  // Admin-defined fields (from /mlm/me), falling back to brochure defaults.
  const formFields = (fields && fields.length ? fields : [
    { key: 'age', label: 'Age', type: 'number', required: false },
    { key: 'contactNo', label: 'Contact No.', type: 'phone', required: true },
    { key: 'refMobile', label: 'Referrer Mobile No.', type: 'phone', required: false },
  ]);

  const set = (k, v) => setFormData((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    for (const f of formFields) {
      if (f.required && !String(formData[f.key] || '').trim()) {
        return toast(`${f.label} is required`, { tone: 'error' });
      }
    }
    setBusy(true);
    try {
      const d = await api.post('/mlm/join', {
        sponsorCode: sponsorCode.trim().toUpperCase() || undefined,
        formData,
      });
      setDone(d);
      refreshUser();
    } catch (err) {
      toast(err.message || 'Please try again.', { title: 'Could not enrol', tone: 'error' });
    }
    setBusy(false);
  };

  const doShare = async (text) => {
    const r = await shareText(text);
    if (r.copied) toast('Copied to clipboard', { tone: 'success' });
  };

  const avatar = user?.profilePhoto || user?.kyc?.selfieImage;

  if (done) {
    return (
      <div style={{
        flex: 1, background: 'var(--primary)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 24,
      }}>
        <div className="card" style={{ borderRadius: 'var(--r-xl)', padding: 26, textAlign: 'center', width: '100%', maxWidth: 420 }}>
          <div style={{ fontSize: 60 }}>🎉</div>
          <h2 style={{ fontSize: 22, fontWeight: 900, marginTop: 10 }}>You are a partner now!</h2>
          <p className="muted" style={{ fontSize: 13.5, marginTop: 8, lineHeight: 1.5 }}>
            Share your referral code to grow your team. You earn {rupee(done.perJoinAmount)} on every join
            (credited after admin approval).
          </p>
          <div style={{
            background: 'var(--primary-soft)', borderRadius: 'var(--r-lg)',
            padding: '16px 24px', marginTop: 18, marginBottom: 4,
          }}>
            <div style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 700 }}>Your referral code</div>
            <div style={{ fontSize: 26, fontWeight: 900, color: 'var(--primary)', letterSpacing: 3, marginTop: 4 }}>
              {done.referralCode}
            </div>
          </div>
          <Button variant="soft" title="Share my code" style={{ marginTop: 12 }}
            onClick={() => doShare(`Join my Narayan team with my referral code: ${done.referralCode}`)} />
          <button onClick={onClose} style={{ marginTop: 16, color: 'var(--primary)', fontWeight: 800, fontSize: 14.5 }}>
            Go to my dashboard →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <TopBar title="Become a Partner" onClose={onClose} />

      <form onSubmit={submit} style={{ padding: 20, paddingBottom: 40 }}>
        <div className="card" style={{ borderRadius: 'var(--r-xl)', padding: 20 }}>
          <div className="row" style={{ gap: 12, marginBottom: 18 }}>
            <div className="grow">
              <div style={{ fontSize: 17, fontWeight: 800 }}>Business Partner Detail</div>
              <p className="muted" style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.4 }}>
                Earn by building your own team. Fill the details below to enrol.
              </p>
            </div>
            <span style={{
              width: 60, height: 74, borderRadius: 8, background: 'var(--bg)',
              border: '1px solid var(--border)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', overflow: 'hidden', flex: 'none',
            }}>
              {avatar ? <img src={avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : '👤'}
            </span>
          </div>

          <ReadonlyField label="Emp. Code" value={user?.staffId || '—'} />
          <ReadonlyField label="Name" value={user?.name || '—'} />

          {formFields.map((f) => (
            <DynField key={f.key} field={f} value={formData[f.key]} onChange={(v) => set(f.key, v)} />
          ))}

          <Field
            label="Sponsor ID / Referral code (optional)" value={sponsorCode} placeholder="NRPXXXXX"
            onChange={(e) => setSponsorCode(e.target.value.toUpperCase())}
          />

          <p className="faint" style={{ fontSize: 11.5, margin: '12px 0', lineHeight: 1.4 }}>
            Leave the sponsor blank if no one referred you. Your unique referral code is generated on enrolment.
          </p>
          <Button type="submit" title={busy ? 'Enrolling…' : 'Enrol as Partner'} disabled={busy} />
        </div>
      </form>
    </div>
  );
}
